<template>
  <div class="UserAvatarMenu">
    <div class="flex">
      <div class="UserAvatarMenu__info">
        <p>{{ user.firstName }} {{ user.lastName }}</p>
      </div>
      <div v-outside="closeDropdown" class="UserAvatarMenu__profile">
        <template v-if="!user.avatar">
          <img
            class="w-full h-full cursor-pointer circle overflow-hidden"
            src="@/assets/images/alien_avatar_space_ufo_icon.png"
            alt="Profile picture placeholder"
            @click="toggleDropdown"
          />
        </template>
        <template v-else>
          <ImageComponent
            :img-url="fullProfileURL(user.avatar)"
            alt="Your profile picture"
            class="cursor-pointer circle overflow-hidden"
            @click="toggleDropdown"
          />
        </template>

        <div v-show="showDropdown" class="UserAvatarMenu__dropdown py-16 pl-16">
          <ul class="UserAvatarMenu__dropdown__list" @click="closeDropdown">
            <li
              v-for="item in dropdownList"
              :key="item.title"
              class="UserAvatarMenu__dropdown__item"
              @click="item.action ? item.action() : null"
            >
              <router-link
                v-if="item.pathName"
                class="color-white font-size-s font-weight-semibold flex items-center gap-8"
                :to="{ name: item.pathName }"
              >
                <span
                  :class="[`icon-${item.icon}`]"
                  class="font-size-base"
                ></span>
                <span>
                  {{ item.title }}
                </span>
              </router-link>
              <button
                v-else
                class="color-white font-size-s font-weight-semibold flex items-center gap-8 cursor-pointer"
                @click="item.action"
              >
                <span
                  :class="[`icon-${item.icon}`]"
                  class="font-size-base"
                ></span>
                <span>
                  {{ item.title }}
                </span>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAuth } from '@/composables/useAuth.js'
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { ImageComponent } from '@snowshoe-frontend/image-component'
import { useI18n } from 'vue-i18n'

const imageproxyURL = import.meta.env.VITE_IMAGEPROXY_URL

const showDropdown = ref(false)
const { signOut } = useAuth()
const userStore = useUserStore()
const { t } = useI18n()
const { user } = storeToRefs(userStore)

const dropdownList = computed(() => [
  {
    title: t('profile'),
    pathName: 'ProfileView',
    icon: 'user',
    action: null
  },
  {
    title: t('change_password'),
    pathName: 'ChangePasswordView',
    icon: 'padlock',
    action: null
  },
  {
    title: t('logout'),
    pathName: null,
    icon: 'logout',
    action: signOut
  }
])

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function closeDropdown() {
  showDropdown.value = false
}

function fullProfileURL(profile_picture_url) {
  return `${imageproxyURL}/insecure/rs:fill:150:150/sm:0/plain/${profile_picture_url}@jpg`
}
</script>

<style lang="scss" scoped>
.UserAvatarMenu {
  &__info {
    color: var(--color-brand-purple);
    font-size: rem(14);
    font-family: var(--font-family-primary);
    font-weight: var(--font-weight-semibold);
    display: flex;
    align-items: center;
    margin-right: rem(16);
  }

  &__profile {
    width: rem(64);
    height: rem(64);
    border-radius: 50%;
    background-color: var(--color-secondary);
    border: 2px solid var(--color-brand-red);
    position: relative;
  }
  &__dropdown {
    position: absolute;
    width: rem(200);
    border-radius: rem(8);
    background-color: var(--color-brand-purple-2);
    color: var(--color-secondary);
    right: 0;
    top: rem(80);
    box-shadow: 0 var(--grid-4) 6px rgba(0, 0, 0, 0.2);

    &::before {
      content: '';
      width: 0;
      height: 0;
      border-left: 12px solid transparent;
      border-right: 12px solid transparent;
      border-bottom: 12px solid var(--color-brand-purple-2);
      position: absolute;
      right: 18px;
      top: -11px;
    }

    &__item {
      &:not(:last-of-type) {
        margin-bottom: var(--grid-8);
      }
      :hover {
        opacity: 0.8;
      }
    }
  }
}
</style>
